import { useEffect, useMemo } from 'react'

import { IntradayInversionAlert } from '~/components/bidding/IntradayInversionAlert'
import { useListBiddingIntradayOrderRowsByDates } from '~/hooks/bidding/useListBiddingIntradayOrders'
import { useNowByMinute } from '~/hooks/useNowByMinute'
import {
  type IntradayCsvInversionCandidate,
  countIntradayCsvInversions,
  intradayCsvInversionTargetDates,
} from '~/pages/IntradayCsvImport/models/csvInversion'

type Props = {
  /** 取り込む行 (`toIntradayCsvInversionCandidates` の戻り値)。 */
  candidates: IntradayCsvInversionCandidate[]
  /**
   * 判定に要る既存の札 (取り込む行の受渡日ぶん) を読み終えた時に、 その `candidates` を
   * 渡して呼ぶ。 呼び出し側は、 読み終えるまで確定へ進ませない (読み終える前は、 既存の札との
   * 売買逆転を警告できない)。
   */
  onExistingOrdersLoaded: (candidates: IntradayCsvInversionCandidate[]) => void
}

/**
 * CSV 取込 Step2 の売買逆転の警告 (spec AC-BCI-035 ※ / intraday-bidding.md AC-BIN-045)。
 * 取り込む行どうし、 または取り込む行と自社の既存の札が売買逆転になる時に、 入札追加タブと
 * 同じバナーを出す。 取込は止めない。
 *
 * 既存の札は、 取り込む行に出てくる受渡日ごとに、 入札追加タブと同じ一覧の query で読む。
 * 一度も読めていない受渡日がある時の取得の失敗は画面のエラーになり、 読めた後の再取得の
 * 失敗では最後に読めた札で判定し続ける。 GC を過ぎたコマの札を外す境界は、 入札追加タブと
 * 同じく 1 分ごとの現在時刻で決める。
 */
export const CsvInversionAlert = ({
  candidates,
  onExistingOrdersLoaded,
}: Props): React.ReactElement => {
  const now = useNowByMinute()
  const targetDates = useMemo(
    () => intradayCsvInversionTargetDates(candidates),
    [candidates],
  )
  const { orders, isLoaded } =
    useListBiddingIntradayOrderRowsByDates(targetDates)
  useEffect(() => {
    if (isLoaded) onExistingOrdersLoaded(candidates)
  }, [isLoaded, candidates, onExistingOrdersLoaded])
  const count = useMemo(
    () => countIntradayCsvInversions(candidates, orders, now),
    [candidates, orders, now],
  )
  return <IntradayInversionAlert count={count} />
}
